import { Component, h } from 'preact'
import Icon from '../assets'
import { AppCardData } from './interfaces'
import {
  isGainsightAppVisible,
  isSalesforceAppVisible,
  isZapierAppVisible,
  isZendeskAppVisible
} from './utils'
import axios from 'axios'

interface Props {}

interface State {
   isZendeskInstalled: boolean,
   isZapierInstalled: boolean,
   isSalesforceInstalled: boolean,
   isSkilljarInstalled: boolean,
   isFreshdeskInstalled: boolean,
   isGainsightInstalled: boolean
}

class IntegrationApps extends Component<Props, State> {
    isZendeskEnabled: boolean
    isZapierEnabled: boolean
    isSalesforceEnabled: boolean
    isGainsightEnabled: boolean
    constructor (props: Props) {
      super(props)
      this.state = {
        isZendeskInstalled: false,
        isZapierInstalled: false,
        isSalesforceInstalled: false,
        isSkilljarInstalled: false,
        isFreshdeskInstalled: false,
        isGainsightInstalled: false
      }
      this.isZendeskEnabled = isZendeskAppVisible()
      this.isZapierEnabled = isZapierAppVisible()
      this.isSalesforceEnabled = isSalesforceAppVisible()
      this.isGainsightEnabled = isGainsightAppVisible()
    }

    async componentDidMount () {
      const [isZendeskInstalled, isSalesforceInstalled, isZapierInstalled, isGainsightInstalled, isSkilljarInstalled, isFreshdeskInstalled] = await Promise.all([this.isZendeskAppInstalled(), this.isSalesforceAppInstalled(), this.isZapierAppInstalled(), this.isGainsightAppInstalled(), this.isSkilljarAppInstalled(), this.isFreshdeskAppInstalled()])
      this.setState({
        isZendeskInstalled: isZendeskInstalled,
        isSalesforceInstalled: isSalesforceInstalled,
        isZapierInstalled: isZapierInstalled,
        isGainsightInstalled: isGainsightInstalled,
        isSkilljarInstalled: isSkilljarInstalled,
        isFreshdeskInstalled: isFreshdeskInstalled
      })
    }

    async isZendeskAppInstalled () {
      if (!this.isZendeskEnabled) return false
      try {
        const res = await axios.get('/integration/zendesk/settings')
        const { apiToken, subdomain, agentEmail } = res.data
        return !!(apiToken && subdomain && agentEmail)
      } catch {
        return false
      }
    }

    async isSalesforceAppInstalled () {
      if (!this.isSalesforceEnabled) return false
      try {
        const res = await axios.get('/integration/salesforce/settings', {
          validateStatus: (state: number) => {
            return (state >= 200 && state < 300) || state === 403
          }
        })
        return Object.keys(res.data).length > 0
      } catch {
        return false
      }
    }

    async isZapierAppInstalled () {
      if (!this.isZapierEnabled) return false
      try {
        const res = await axios.get('/integration/zapier/settings')
        return 'key' in res.data
      } catch {
        return false
      }
    }

    async isSkilljarAppInstalled () {
      try {
        const res = await axios.get('/integration/skilljar/settings')
        return 'apiToken' in res.data
      } catch {
        return false
      }
    }

    async isFreshdeskAppInstalled () {
      try {
        const res = await axios.get('/integration/freshdesk/settings')
        return 'apiToken' in res.data
      } catch {
        return false
      }
    }

    async isGainsightAppInstalled () {
      if (!this.isGainsightEnabled) return false
      try {
        const res = await axios.get('/integration/gainsight/settings', {
          validateStatus: (state: number) => {
            return (state >= 200 && state < 300) || state === 403
          }
        })
        return Object.keys(res.data).length > 0
      } catch {
        return false
      }
    }

    render (_: Props, { isZendeskInstalled, isSalesforceInstalled, isZapierInstalled, isGainsightInstalled, isSkilljarInstalled, isFreshdeskInstalled }: State) {
      return (
        <div className='integration-apps-container'>
          <AppCard title='Zendesk'
            iconName='appIconZendesk'
            appLink='/settings/integrations/zendesk'
            isVisible={this.isZendeskEnabled}
            isInstalled={isZendeskInstalled}
            description='Streamline your customer support workflow: Escalate tickets and enable federated search with Zendesk.' />
          <AppCard title='Salesforce'
            iconName='appIconSalesforce'
            appLink='/settings/integrations/salesforce'
            appLogoCustomClass='app-card-logo-with-border'
            isVisible={this.isSalesforceEnabled}
            isInstalled={isSalesforceInstalled}
            description='Push community member and activity data to Salesforce to gain insights via a 360 degree customer view.' />
          <AppCard title='Zapier'
            iconName='appIconZapier'
            appLink='/settings/integrations/zapier'
            isVisible={this.isZapierEnabled}
            isInstalled={isZapierInstalled}
            description='Connect inSided with thousands of other apps like Slack and JIRA to build workflows, automate repetitive tasks and save time.' />
          <AppCard title='Gainsight'
            iconName='appIconGainsight'
            appLink='/settings/integrations/gainsight'
            isVisible={this.isGainsightEnabled}
            isInstalled={isGainsightInstalled}
            description='Boost customer success with a 360-degree view of your community members & their activity in Gainsight.' />
          <AppCard title='Skilljar'
            iconName='appIconSkilljar'
            appLink='/settings/integrations/skilljar'
            isVisible
            isInstalled={isSkilljarInstalled}
            description='Search for Skilljar courses through your inSided platform with federated search.' />
          <AppCard title='Freshdesk'
            iconName='appIconFreshdesk'
            appLink='/settings/integrations/freshdesk'
            appLogoCustomClass='app-card-logo-with-border'
            isVisible
            isInstalled={isFreshdeskInstalled}
            description='Search for Freshdesk articles through your inSided platform with federated search.' />
        </div>
      )
    }
}

export const AppCard = ({ iconName, title, isInstalled, description, appLink, isVisible, appLogoCustomClass }: AppCardData) => {
  return (
    isVisible ? <a className='integration-app-card-container' href={appLink}>
      <div className='app-card-header-container'>
        <Icon customClass={`app-card-logo ${appLogoCustomClass ?? ''}`} icon={iconName} width={48} height={48} />
        <div className='app-card-title-wrapper'>
          <div className='app-card-title'>{title}</div>
          {isInstalled && <div>
            <span><Icon customClass='app-installed-logo' icon='checkmark' width={13} height={13} /></span>
            <span className='app-installed-text'>Installed</span>
          </div>}
        </div>
      </div>
      <div className='app-description-container'>
        {description}
      </div>
    </a> : null
  )
}

export default IntegrationApps
